/**
 * Actions on a live call — hand the caller to a teammate, or put music on their
 * line while they are held.
 *
 * Routed through the operations proxy at `/api/voice/calls/:callControlId/…`,
 * like call history: operations forwards the caller's notifi ES256 Bearer +
 * `x-tenant-id` to the calling service, which owns the live call. The browser
 * never talks to the CS directly.
 */

import { apiGet, apiPost, operationsClient } from '@/lib/api';
import type {
  HoldAudioResult,
  TransferMode,
  TransferResult,
  TransferTargetRef,
  TransferTargetsResponse,
  WarmTransferStatusResponse,
} from './transfer.types';

const BASE = '/api/voice/calls';

/** A call-control id is base64-ish (`v3:AbC/dEf+gh==`) and lands in a path
 *  segment, so a raw `/` in it would silently address a different route. */
const transferUrl = {
  targets: (callControlId: string) =>
    `${BASE}/${encodeURIComponent(callControlId)}/transfer/targets`,
  transfer: (callControlId: string) => `${BASE}/${encodeURIComponent(callControlId)}/transfer`,
  warm: (callControlId: string, action: 'status' | 'complete' | 'cancel') =>
    `${BASE}/${encodeURIComponent(callControlId)}/transfer/${action}`,
  holdAudio: (callControlId: string) => `${BASE}/${encodeURIComponent(callControlId)}/hold-audio`,
};

/** The one body field that names the target. */
function transferTargetBody(target: TransferTargetRef): Record<string, string> {
  if ('landlineId' in target) return { targetLandlineId: target.landlineId };
  if ('directoryEntryId' in target) return { targetDirectoryEntryId: target.directoryEntryId };
  if ('number' in target) return { targetNumber: target.number };
  return { targetUserId: target.userId };
}

export const callTransferApi = {
  /** Already sorted by the CS: reachable people first, then by name. */
  targets: (callControlId: string) =>
    apiGet<TransferTargetsResponse>(operationsClient, transferUrl.targets(callControlId)),

  /**
   * Direct (the default): IRREVERSIBLE. On success the agent's own leg is hung
   * up by the server and the customer is re-dialled at the target.
   *
   * `mode: 'warm'` (ask first): the target rings while the agent stays on; the
   * transfer is then completed or cancelled below. The mode is only sent when
   * asked for, so a direct transfer is the same request it always was.
   *
   * Exactly one target, matching the server: a teammate rings on every device
   * they own; a desk phone is one registration belonging to nobody; an outside
   * number is either saved in the directory or typed by an admin.
   */
  transfer: (callControlId: string, target: TransferTargetRef, mode?: TransferMode) =>
    apiPost<TransferResult>(operationsClient, transferUrl.transfer(callControlId), {
      ...transferTargetBody(target),
      ...(mode === 'warm' ? { mode } : {}),
    }),

  /** Where an ask-first transfer stands. */
  warmStatus: (callControlId: string) =>
    apiGet<WarmTransferStatusResponse>(operationsClient, transferUrl.warm(callControlId, 'status')),

  /** Ask first: hand the caller to the teammate the agent is talking to. */
  completeWarm: (callControlId: string) =>
    apiPost<WarmTransferStatusResponse>(
      operationsClient,
      transferUrl.warm(callControlId, 'complete'),
      {},
    ),

  /**
   * Hold music on the caller's line — NOT the hold itself.
   *
   * The hold is a WebRTC re-INVITE from this browser and never reaches the
   * server, which is why a held caller has always heard silence: nothing on the
   * call-control side knew to play anything. This tells the side that can.
   *
   * Called ALONGSIDE the client-side hold and never waited on. A failure leaves
   * the caller held in silence, which is what they got before this existed, so
   * there is nothing for a caller of this to do about it.
   */
  holdAudio: (callControlId: string, onHold: boolean, clientRef?: string) =>
    apiPost<HoldAudioResult>(operationsClient, transferUrl.holdAudio(callControlId), {
      onHold,
      ...(clientRef ? { clientRef } : {}),
    }),

  /** Ask first: stop, and go back to the caller. */
  cancelWarm: (callControlId: string) =>
    apiPost<WarmTransferStatusResponse>(
      operationsClient,
      transferUrl.warm(callControlId, 'cancel'),
      {},
    ),
};
